/**
 * Advances `currentDate` inside the render loop. Frame-rate independent: the date moves by speed × real time.
 * Speed changes while playing are eased in log space (Sprint 2, S2-05) — the throttle never jumps.
 */
import { get } from 'svelte/store';
import { approachLog } from '../core/easing';
import { nearestFlyby, slowmoCap } from '../core/slowmo';
import { currentDate, flybyTimes, playback, setPlaying, slowmoSkip, timeRange } from './stores';

/** Maximum real time step per frame (s); longer gaps (e.g. a background tab) are not replayed. */
const MAX_DT = 0.25;
/** Time constant of the speed easing, s (≈ 0.6 s to settle on a new detent). */
const SPEED_TAU = 0.15;

/** The speed actually applied (ms of date per s), easing towards `playback.speed`; NaN = snap on next frame. */
let effective = Number.NaN;

/** Speed currently applied by playback (ms of date per real second). */
export function effectiveSpeed(): number {
  return Number.isNaN(effective) ? get(playback).speed : effective;
}

/** Automatic slow-motion cap at `nowMs` (S2-11): Infinity when none applies or the user skipped this flyby. */
export function slowmoLimit(nowMs: number): number {
  const times = get(flybyTimes);
  const skip = get(slowmoSkip);
  if (skip !== null && nearestFlyby(nowMs, times) === skip) return Infinity;
  return slowmoCap(nowMs, times);
}

/** Whether slow-motion currently holds playback below the throttle speed. */
export function slowmoActive(nowMs: number): boolean {
  const p = get(playback);
  return p.playing && slowmoLimit(nowMs) < p.speed;
}

/** Skips the slow-motion of the flyby nearest to `nowMs` (one tap on the SLOW-MO badge). */
export function skipSlowmo(nowMs: number): void {
  const t = nearestFlyby(nowMs, get(flybyTimes));
  if (!Number.isNaN(t)) slowmoSkip.set(t);
}

/** Forgets the eased speed, so the next frame starts at the target speed (tests, deep links). */
export function resetSpeedEasing(): void {
  effective = Number.NaN;
}

export function advancePlayback(dtSeconds: number): void {
  const p = get(playback);
  if (!p.playing) {
    effective = p.speed; // starting playback is immediate; only changes while playing are eased
    return;
  }
  const dt = Math.min(Math.max(dtSeconds, 0), MAX_DT);
  const target = Math.min(p.speed, slowmoLimit(get(currentDate)));
  effective = Number.isNaN(effective) ? target : approachLog(effective, target, dt, SPEED_TAU);
  const { min, max } = get(timeRange);
  const next = get(currentDate) + (p.reverse ? -1 : 1) * effective * dt * 1000;
  if (next >= max) {
    currentDate.set(max);
    setPlaying(false);
  } else if (next <= min) {
    currentDate.set(min);
    setPlaying(false);
  } else {
    currentDate.set(next);
  }
}
